import type { Row } from "./api";
import {
  parseFilingInterview,
  deriveFilingInterview,
  filingInterviewValid,
  validReturnBuilderDraft,
  type ReturnBuilderDraft,
} from "./filing-status";

export {
  RETURN_BUILDER_PROFILE_IDS,
  type ReturnBuilderDraft,
  type ReturnBuilderProfileId,
} from "./filing-status";
export const validateReturnBuilderDraft = validReturnBuilderDraft;

const object = (value: unknown): value is Row =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const own = (value: Row, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);
const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

function assertReadable(raw: unknown): void {
  if (
    object(raw) &&
    own(raw, "interview") &&
    !filingInterviewValid(raw.interview)
  )
    throw new Error(
      "Return profile — Saved information: This saved interview cannot be edited safely. Ask an administrator to review it; no information has been replaced.",
    );
}

/** Missing is an unconfirmed UI profile, not a negative answer to any tax question. Reading never saves a value. */
export function readReturnBuilderDraft(raw: unknown): ReturnBuilderDraft {
  assertReadable(raw);
  const interview = parseFilingInterview(raw);
  return interview.returnBuilder
    ? clone(interview.returnBuilder)
    : { version: 1, profile: { version: 1, selected: [], confirmed: false } };
}

/** Explicit UI edit using the existing Q-008 draft adapter. No identity facts, verification artifacts, or source contracts are written. */
export function withReturnBuilderDraft(
  raw: unknown,
  draft: ReturnBuilderDraft,
  selected: Row,
): unknown {
  assertReadable(raw);
  if (!validateReturnBuilderDraft(draft))
    throw new Error(
      "Return profile — Draft information: The profile contains unsupported choices or invalid field lengths. Review the profile and taxpayer details before saving.",
    );
  const interview = {
    ...parseFilingInterview(raw),
    version: 2 as const,
    returnBuilder: clone(draft),
  };
  return deriveFilingInterview(interview, selected, raw).answer;
}

/** Formatting only. Typographical letters and overlong numbers remain visible for field validation; no verification is performed. */
export function formatTaxId(value: string): string {
  if (/[^\d -]/.test(value)) return value;
  const digits = value.replace(/[ -]/g, "");
  if (digits.length > 9) return value;
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}
